import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertCircle, AlertTriangle, Eye, EyeOff, Image as ImageIcon, Loader2, Plus, ShoppingBag, Trash2 } from 'lucide-react';
import {
  basculerVitrineBoutique,
  creerArticleBoutique,
  fetchBoutiqueArticles,
  modifierArticleBoutique,
  supprimerArticleBoutique,
  uploadImageArticleBoutique,
  type ArticleBoutique,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { euros } from '../utils/format';
import SaisieTailles from '../components/SaisieTailles';

/**
 * Le catalogue de la boutique : ce qu'on vend, à quel prix, en quelle
 * quantité.
 *
 * Le catalogue et les commandes vivaient sur le même écran, à deux onglets.
 * Ce sont pourtant deux métiers et deux moments : on règle un catalogue une
 * fois par saison, et on relève les commandes toutes les semaines. Le
 * catalogue est donc passé en Administration, avec les autres réglages, et
 * les commandes en Comptabilité, où le trésorier les cherche.
 */
export default function AdminBoutiquePage() {
  const { isLoading: authLoading, aLeDroit } = useAuth();
  const peutVoir = aLeDroit('voir_boutique');
  const peutGerer = aLeDroit('gerer_boutique');
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);

  const articlesQuery = useQuery({
    queryKey: ['bureau-boutique-articles'],
    queryFn: fetchBoutiqueArticles,
    enabled: peutVoir,
  });

  const rafraichir = () => queryClient.invalidateQueries({ queryKey: ['bureau-boutique-articles'] });

  if (authLoading) {
    return <div className="p-8 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary-600" /></div>;
  }

  if (!peutVoir) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <div>
            <p className="font-semibold">Accès réservé</p>
            <p className="text-sm mt-1">La boutique dépend de votre fonction au bureau : un administrateur peut l'ouvrir dans la grille de droits.</p>
          </div>
        </div>
      </div>
    );
  }

  const active = articlesQuery.data?.active ?? false;
  const enLigne = articlesQuery.data?.enLigne ?? true;
  const ouverte = articlesQuery.data?.vitrineOuverte ?? false;
  const publies = articlesQuery.data?.articlesPublies ?? 0;
  const articles = articlesQuery.data?.articles ?? [];

  return (
    <div className="p-4 lg:p-0 space-y-5 pb-24 lg:pb-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <ShoppingBag className="w-7 h-7 text-primary-700" />
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">Boutique</h1>
            <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
              Le catalogue et les stocks. Les commandes se suivent en Comptabilité.
            </p>
          </div>
        </div>

        {/* L'état de la vitrine, lisible avant tout le reste : on ouvre cet
            écran pour saisir, et savoir si le public regarde pendant qu'on
            saisit change la façon dont on s'y prend. */}
        {active && (
          <span
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold ${
              ouverte
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
            }`}
          >
            {ouverte ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            {ouverte ? 'Boutique ouverte' : 'Boutique fermée'}
          </span>
        )}
      </div>

      {!active && (
        <div className="card bg-amber-50 border-amber-200 text-amber-800 text-sm">
          L'option boutique n'est pas active : la vitrine publique est fermée. Vous pouvez préparer
          le catalogue — il s'affichera dès la souscription de l'option (20 €/mois ou 200 €/an).
        </div>
      )}

      {/* L'encaissement avant l'interrupteur, et c'est l'ordre qui compte :
          ouvrir une boutique qui ne peut rien encaisser est une erreur plus
          coûteuse que de la laisser fermée un jour de plus. */}
      {active && <AvertissementEncaissement encaissement={articlesQuery.data?.encaissement} />}

      {active && (
        <VitrineInterrupteur
          enLigne={enLigne}
          publies={publies}
          peutGerer={peutGerer}
          onFait={(message) => {
            setNotice(message);
            rafraichir();
          }}
        />
      )}

      {notice && <p className="text-sm rounded-xl px-3 py-2 bg-primary-50 text-primary-800">{notice}</p>}

      {articlesQuery.isPending ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary-600" /></div>
      ) : (
        <Articles articles={articles} peutGerer={peutGerer} onChange={rafraichir} onNotice={setNotice} />
      )}
    </div>
  );
}

/**
 * L'avertissement d'encaissement.
 *
 * Tout se règle par carte dans cette boutique. Sans compte de paiement relié,
 * la vitrine peut s'ouvrir, les articles s'afficher, le panier se remplir — et
 * le paiement ne s'ouvrira jamais. Le bureau ne s'en aperçoit qu'au premier
 * acheteur qui écrit pour dire que ça ne marche pas, et il a alors annoncé sa
 * boutique à tout le village.
 *
 * L'écran le dit donc avant l'interrupteur, et il ne s'efface pas : ce n'est
 * pas un conseil, c'est une condition.
 */
function AvertissementEncaissement({
  encaissement,
}: {
  encaissement?: { configure: boolean; modeTest: boolean };
}) {
  if (encaissement === undefined || (encaissement.configure && !encaissement.modeTest)) {
    return null;
  }

  if (!encaissement.configure) {
    return (
      <div className="card bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
        <div className="text-sm text-red-900 dark:text-red-200 space-y-1">
          <p className="font-semibold">
            Aucun encaissement possible : le compte de paiement n'est pas configuré.
          </p>
          <p>
            Tout se règle par carte dans cette boutique. En l'état, un visiteur pourra choisir ses
            articles et remplir son panier, mais <strong>le paiement ne s'ouvrira jamais</strong> —
            et vous ne le saurez qu'au premier acheteur qui écrira pour dire que ça ne marche pas.
          </p>
          <p>
            Reliez le compte de l'association dans{' '}
            <Link to="/admin/parametres" className="underline font-medium">
              Paramètres → Encaissement en ligne
            </Link>
            , ou gardez la boutique fermée en attendant. Le guide « Mettre en place l'encaissement
            en ligne » est dans vos{' '}
            <Link to="/guides" className="underline font-medium">
              guides
            </Link>
            .
          </p>
        </div>
      </div>
    );
  }

  // Configuré, mais avec des clés de test : pire que rien, parce que tout a
  // l'air de fonctionner. Le paiement s'ouvre, la commande s'enregistre, et
  // pas un euro n'arrive sur le compte de l'association.
  return (
    <div className="card bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800 flex items-start gap-3">
      <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
      <div className="text-sm text-amber-900 dark:text-amber-200 space-y-1">
        <p className="font-semibold">Encaissement en mode test : aucun euro n'arrivera vraiment.</p>
        <p>
          Les clés configurées sont des clés d'essai. Le paiement s'ouvrira, la commande
          s'enregistrera, l'acheteur recevra sa confirmation — et{' '}
          <strong>rien ne sera encaissé sur le compte de l'association</strong>. C'est plus
          trompeur qu'une absence de configuration, puisque tout a l'air de fonctionner.
        </p>
        <p>
          Remplacez les clés d'essai par celles du compte réel dans{' '}
          <Link to="/admin/parametres" className="underline font-medium">
            Paramètres → Encaissement en ligne
          </Link>{' '}
          avant d'ouvrir la boutique.
        </p>
      </div>
    </div>
  );
}

/**
 * L'interrupteur de la vitrine.
 *
 * Il ne touche pas au catalogue : fermer ne désactive aucun article, rouvrir
 * n'en publie aucun. C'est ce qui permet de préparer une saison entière puis
 * d'ouvrir d'un geste, et de refermer après la fête sans rien perdre.
 */
function VitrineInterrupteur({
  enLigne,
  publies,
  peutGerer,
  onFait,
}: {
  enLigne: boolean;
  publies: number;
  peutGerer: boolean;
  onFait: (message: string) => void;
}) {
  const [erreur, setErreur] = useState<string | null>(null);

  const basculer = useMutation({
    mutationFn: (ouvrir: boolean) => basculerVitrineBoutique(ouvrir),
    onSuccess: (r: any) => {
      setErreur(null);
      onFait(r.message);
    },
    onError: (e: Error) => setErreur(e.message),
  });

  return (
    <div className="card flex flex-wrap items-center gap-4">
      <div className="flex-1 min-w-[16rem]">
        <p className="font-medium text-gray-900 dark:text-gray-100">
          {enLigne ? 'La vitrine répond au public' : 'La vitrine ne répond plus'}
        </p>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
          {enLigne
            ? "Fermez-la le temps de saisir vos articles : le catalogue reste intact, seuls les visiteurs ne le voient plus."
            : "Vos articles sont conservés. Rouvrez quand le catalogue est prêt — rien n'est publié entre-temps."}
        </p>
        {/* Ouvrir une vitrine sans article n'est pas une erreur, mais c'est
            rarement ce qu'on voulait faire. */}
        {enLigne && publies === 0 && (
          <p className="text-sm text-amber-700 dark:text-amber-400 mt-1.5">
            Aucun article publié pour l'instant : la vitrine est ouverte, mais vide.
          </p>
        )}
        {erreur && <p className="text-sm text-red-600 mt-1.5">{erreur}</p>}
      </div>

      {peutGerer ? (
        <button
          type="button"
          onClick={() => basculer.mutate(!enLigne)}
          disabled={basculer.isPending}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50 ${
            enLigne ? 'bg-gray-600 hover:bg-gray-700' : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          {basculer.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : enLigne ? (
            <EyeOff className="w-4 h-4" />
          ) : (
            <Eye className="w-4 h-4" />
          )}
          {enLigne ? 'Fermer la boutique' : 'Ouvrir la boutique'}
        </button>
      ) : (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Seule une fonction disposant de la gestion de la boutique peut l'ouvrir ou la fermer.
        </p>
      )}
    </div>
  );
}

function Articles({ articles, peutGerer, onChange, onNotice }: {
  articles: ArticleBoutique[];
  peutGerer: boolean;
  onChange: () => void;
  onNotice: (n: string | null) => void;
}) {
  const [nouveau, setNouveau] = useState({ nom: '', prix: '', stock: '' });
  const creer = useMutation({
    mutationFn: () => creerArticleBoutique({ nom: nouveau.nom, prix: parseFloat(nouveau.prix || '0'), stock: parseInt(nouveau.stock || '0', 10) }),
    onSuccess: () => { setNouveau({ nom: '', prix: '', stock: '' }); onChange(); },
    onError: (e: Error) => onNotice(e.message),
  });

  return (
    <div className="space-y-3">
      {peutGerer && (
        <div className="card flex flex-wrap items-end gap-2">
          <label className="text-xs text-gray-500 dark:text-gray-400 flex-1 min-w-[10rem]">Nouvel article
            <input className="input mt-1" placeholder="T-shirt de la fête…" value={nouveau.nom} onChange={(e) => setNouveau({ ...nouveau, nom: e.target.value })} />
          </label>
          <label className="text-xs text-gray-500 dark:text-gray-400">Prix €
            <input className="input mt-1 w-24" inputMode="decimal" value={nouveau.prix} onChange={(e) => setNouveau({ ...nouveau, prix: e.target.value })} />
          </label>
          <label className="text-xs text-gray-500 dark:text-gray-400">Stock
            <input className="input mt-1 w-24" inputMode="numeric" value={nouveau.stock} onChange={(e) => setNouveau({ ...nouveau, stock: e.target.value })} />
          </label>
          <button onClick={() => creer.mutate()} disabled={creer.isPending || !nouveau.nom.trim()} className="btn-primary inline-flex items-center gap-1.5 disabled:opacity-50">
            <Plus className="w-4 h-4" /> Ajouter
          </button>
        </div>
      )}
      {articles.map((a) => (
        <LigneArticle key={a.id} article={a} peutGerer={peutGerer} onChange={onChange} onNotice={onNotice} />
      ))}
      {articles.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400">Aucun article : ajoutez le premier.</p>}
    </div>
  );
}

function LigneArticle({ article, peutGerer, onChange, onNotice }: {
  article: ArticleBoutique;
  peutGerer: boolean;
  onChange: () => void;
  onNotice: (n: string | null) => void;
}) {
  const [form, setForm] = useState({
    nom: article.nom,
    description: article.description ?? '',
    prix: String(article.prix),
    stock: String(article.stock),
    tailles: [...(article.tailles ?? [])],
    partExpedition: String(article.partExpedition ?? 1),
    actif: article.actif,
  });
  const fichierRef = useRef<HTMLInputElement>(null);

  const enregistrer = useMutation({
    mutationFn: () => modifierArticleBoutique(article.id, {
      nom: form.nom,
      description: form.description,
      prix: parseFloat(form.prix || '0'),
      stock: parseInt(form.stock || '0', 10),
      // L'API accepte la liste sous forme de texte : elle la redécoupe et la
      // dédoublonne de son côté, quelle que soit la façon dont on l'a saisie.
      tailles: form.tailles.join(', '),
      partExpedition: parseInt(form.partExpedition || '1', 10),
      actif: form.actif,
    }),
    onSuccess: () => { onNotice('Article enregistré.'); onChange(); },
    onError: (e: Error) => onNotice(e.message),
  });

  const supprimer = useMutation({
    mutationFn: () => supprimerArticleBoutique(article.id),
    onSuccess: onChange,
    onError: (e: Error) => onNotice(e.message),
  });

  const uploader = useMutation({
    mutationFn: (f: File) => uploadImageArticleBoutique(article.id, f),
    onSuccess: () => { onNotice('Visuel mis à jour.'); onChange(); },
    onError: (e: Error) => onNotice(e.message),
  });

  return (
    <div className="card flex flex-wrap items-center gap-3">
      <button
        type="button"
        title={peutGerer ? 'Changer le visuel' : undefined}
        onClick={() => peutGerer && fichierRef.current?.click()}
        className="w-16 h-16 rounded-xl overflow-hidden bg-primary-50 dark:bg-primary-900/20 grid place-items-center shrink-0"
      >
        {article.imageUrl ? (
          <img src={article.imageUrl} alt={article.nom} className="w-full h-full object-cover" />
        ) : (
          <ImageIcon className="w-6 h-6 text-primary-300" />
        )}
      </button>
      <input ref={fichierRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploader.mutate(f); e.target.value = ''; }} />

      {peutGerer ? (
        <>
          <input className="input flex-1 min-w-[10rem]" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} />
          <input className="input flex-[2] min-w-[12rem]" placeholder="Description courte" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <label className="text-xs text-gray-500">€
            <input className="input w-20" inputMode="decimal" value={form.prix} onChange={(e) => setForm({ ...form, prix: e.target.value })} />
          </label>
          <label className="text-xs text-gray-500">Stock
            <input className="input w-20" inputMode="numeric" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} />
          </label>
          <label className="text-xs text-gray-500" title="Ce que l'article pèse dans le colis. 1 pour un tee-shirt, 2 pour une veste, 0 pour ce qui se glisse dans l'envoi des autres.">
            Colis
            <input className="input w-16" inputMode="numeric" value={form.partExpedition} onChange={(e) => setForm({ ...form, partExpedition: e.target.value })} />
          </label>
          <label className="text-xs text-gray-500 flex items-center gap-1.5">
            <input type="checkbox" checked={form.actif} onChange={(e) => setForm({ ...form, actif: e.target.checked })} className="accent-primary-600" />
            En vitrine
          </label>
          <button onClick={() => enregistrer.mutate()} disabled={enregistrer.isPending} className="btn-primary text-sm px-3 py-1.5 disabled:opacity-50">OK</button>
          <button onClick={() => window.confirm(`Supprimer « ${article.nom} » ? Les commandes passées gardent leurs lignes.`) && supprimer.mutate()}
            className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg" title="Supprimer">
            <Trash2 className="w-4 h-4" />
          </button>

          {/* Les tailles sur leur propre ligne : c'est le seul réglage de
              longueur variable, et le même geste que le bloc « Tailles de
              vêtements » des Paramètres. Aucune taille déclarée = la question
              n'est pas posée à l'acheteur — un mug n'a pas de taille. */}
          <label className="basis-full text-xs text-gray-500 flex flex-wrap items-center gap-2">
            <span className="shrink-0">Tailles proposées</span>
            <SaisieTailles
              tailles={form.tailles}
              onChange={(tailles) => setForm({ ...form, tailles })}
            />
            {form.tailles.length === 0 && (
              <span className="text-gray-400">— taille unique, la question n'est pas posée</span>
            )}
          </label>
        </>
      ) : (
        <>
          <p className="font-medium text-gray-900 dark:text-gray-100 flex-1">{article.nom}</p>
          <p className="tabular-nums">{euros.format(article.prix)}</p>
          <p className="text-sm text-gray-500">stock {article.stock}</p>
        </>
      )}
    </div>
  );
}
